import { useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import clsx from "clsx";
import { Button, Card, Modal, Text, Tooltip } from "@marekl/adminer/components";
import {
  BLOCK_DEFS,
  CHILD_PALETTE,
  PALETTE,
  blockSummary,
  createBlock,
  type EditorBlock,
  type EditorDoc,
} from "./blocksModel";
import { BlockPropsForm } from "./BlockPropsForm";
import styles from "./TemplateDetailPage.module.css";

/** Address of a block: top-level index, optionally a child index inside it. */
interface BlockPath {
  index: number;
  child: number | null;
}

function pathEquals(a: BlockPath | null, b: BlockPath): boolean {
  return !!a && a.index === b.index && a.child === b.child;
}

interface BlocksEditorProps {
  doc: EditorDoc;
  onChange: (doc: EditorDoc) => void;
}

/**
 * WYSIWYG side of the blocks editor: vertical block list with selection,
 * up/down reordering, delete, an add-block palette (one nesting level for
 * repeat children) and the props form for the selected block.
 */
export function BlocksEditor({ doc, onChange }: BlocksEditorProps) {
  const [selected, setSelected] = useState<BlockPath | null>(null);
  /** Palette destination: `null` closed; `{parent: null}` top level. */
  const [paletteParent, setPaletteParent] = useState<number | null | false>(false);

  function getBlock(path: BlockPath): EditorBlock | undefined {
    const top = doc.blocks[path.index];
    if (!top) return undefined;
    return path.child === null ? top : top.children?.[path.child];
  }

  function replaceTop(index: number, next: EditorBlock): void {
    const blocks = doc.blocks.slice();
    blocks[index] = next;
    onChange({ blocks });
  }

  function updateBlock(path: BlockPath, next: EditorBlock): void {
    const top = doc.blocks[path.index];
    if (!top) return;
    if (path.child === null) {
      replaceTop(path.index, next);
    } else {
      const children = (top.children ?? []).slice();
      children[path.child] = next;
      replaceTop(path.index, { ...top, children });
    }
  }

  function addBlock(type: string, parent: number | null): void {
    const block = createBlock(type);
    if (parent === null) {
      onChange({ blocks: [...doc.blocks, block] });
      setSelected({ index: doc.blocks.length, child: null });
    } else {
      const top = doc.blocks[parent];
      if (!top) return;
      const children = [...(top.children ?? []), block];
      replaceTop(parent, { ...top, children });
      setSelected({ index: parent, child: children.length - 1 });
    }
    setPaletteParent(false);
  }

  function deleteBlock(path: BlockPath): void {
    if (path.child === null) {
      onChange({ blocks: doc.blocks.filter((_, i) => i !== path.index) });
    } else {
      const top = doc.blocks[path.index];
      if (!top) return;
      replaceTop(path.index, {
        ...top,
        children: (top.children ?? []).filter((_, i) => i !== path.child),
      });
    }
    setSelected(null);
  }

  function moveBlock(path: BlockPath, dir: -1 | 1): void {
    if (path.child === null) {
      const to = path.index + dir;
      if (to < 0 || to >= doc.blocks.length) return;
      const blocks = doc.blocks.slice();
      [blocks[path.index], blocks[to]] = [blocks[to], blocks[path.index]];
      onChange({ blocks });
      setSelected({ index: to, child: null });
    } else {
      const top = doc.blocks[path.index];
      const children = (top?.children ?? []).slice();
      const to = path.child + dir;
      if (!top || to < 0 || to >= children.length) return;
      [children[path.child], children[to]] = [children[to], children[path.child]];
      replaceTop(path.index, { ...top, children });
      setSelected({ index: path.index, child: to });
    }
  }

  function renderCard(
    block: EditorBlock,
    path: BlockPath,
    count: number,
  ): JSX.Element {
    const def = BLOCK_DEFS[block.type];
    const Icon = def?.icon;
    const pos = path.child === null ? path.index : path.child;
    const summary = blockSummary(block);
    const isSelected = pathEquals(selected, path);

    return (
      <div key={block.uid}>
        <div
          className={clsx(styles.blockCard, isSelected && styles.blockCardSelected)}
          role="button"
          tabIndex={0}
          onClick={() => setSelected(path)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setSelected(path);
            }
          }}
        >
          {Icon && <Icon size={16} aria-hidden="true" className={styles.blockIcon} />}
          <div className={styles.blockCardBody}>
            <span className={styles.blockLabel}>{def?.label ?? block.type}</span>
            {summary && (
              <Tooltip content={summary}>
                <span className={styles.blockSummary}>{summary}</span>
              </Tooltip>
            )}
          </div>
          <div
            className={styles.blockActions}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip content="Move up">
              <Button
                size="sm"
                weight="ghost"
                iconOnly
                aria-label="Move block up"
                startIcon={<ArrowUp size={14} />}
                disabled={pos === 0}
                onClick={() => moveBlock(path, -1)}
              />
            </Tooltip>
            <Tooltip content="Move down">
              <Button
                size="sm"
                weight="ghost"
                iconOnly
                aria-label="Move block down"
                startIcon={<ArrowDown size={14} />}
                disabled={pos === count - 1}
                onClick={() => moveBlock(path, 1)}
              />
            </Tooltip>
            <Tooltip content="Delete block">
              <Button
                size="sm"
                weight="ghost"
                iconOnly
                aria-label="Delete block"
                startIcon={<Trash2 size={14} />}
                onClick={() => deleteBlock(path)}
              />
            </Tooltip>
          </div>
        </div>

        {block.type === "repeat" && path.child === null && (
          <div className={styles.repeatChildren}>
            {(block.children ?? []).length === 0 && (
              <Text variant="muted">The repeat has no inner blocks yet.</Text>
            )}
            {(block.children ?? []).map((child, ci) =>
              renderCard(
                child,
                { index: path.index, child: ci },
                block.children?.length ?? 0,
              ),
            )}
            <Button
              size="sm"
              variant="primary" weight="outline"
              onClick={() => setPaletteParent(path.index)}
            >
              <Plus size={14} aria-hidden="true" />
              Add inner block
            </Button>
          </div>
        )}
      </div>
    );
  }

  const selectedBlock = selected ? getBlock(selected) : undefined;
  const selectedDef = selectedBlock ? BLOCK_DEFS[selectedBlock.type] : undefined;

  return (
    <>
      <Card>
        <Card.Header title="Blocks" />
        <Card.Content>
          <div className={styles.blockList}>
            {doc.blocks.length === 0 && (
              <Text variant="muted">
                The template has no blocks yet. Start with the button below.
              </Text>
            )}
            {doc.blocks.map((block, i) =>
              renderCard(block, { index: i, child: null }, doc.blocks.length),
            )}
            <Button variant="primary" weight="outline" onClick={() => setPaletteParent(null)}>
              <Plus size={14} aria-hidden="true" />
              Add block
            </Button>
          </div>
        </Card.Content>
      </Card>

      {selectedBlock && selectedDef && (
        <Card>
          <Card.Header
            title={`Properties — ${selectedDef.label}`}
            actions={
              <Button size="sm" weight="ghost" onClick={() => setSelected(null)}>
                Close
              </Button>
            }
          />
          <Card.Content>
            <BlockPropsForm
              block={selectedBlock}
              onChange={(next) => selected && updateBlock(selected, next)}
            />
          </Card.Content>
        </Card>
      )}

      {paletteParent !== false && (
        <Modal
          open
          title={paletteParent === null ? "Add block" : "Add inner block"}
          onClose={() => setPaletteParent(false)}
          footer={<Button onClick={() => setPaletteParent(false)}>Cancel</Button>}
        >
          <div className={styles.palette}>
            {(paletteParent === null ? PALETTE : CHILD_PALETTE).map((type) => {
              const def = BLOCK_DEFS[type];
              const Icon = def.icon;
              return (
                // A palette tile: a vertically stacked icon + label + note
                // grid cell. <Button> is a control-height row and cannot stack
                // its content, so the tile stays a native button.
                <button
                  data-composed-control
                  key={type}
                  type="button"
                  className={styles.paletteItem}
                  onClick={() => addBlock(type, paletteParent)}
                >
                  <Icon size={18} aria-hidden="true" />
                  <span>{def.label}</span>
                  {def.advanced && (
                    <span className={styles.paletteNote}>Advanced users only</span>
                  )}
                </button>
              );
            })}
          </div>
        </Modal>
      )}
    </>
  );
}
